import { createReducer, on } from '@ngrx/store';
import * as RulesActions from '../actions/rules.actions';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Rule } from '../../models/rule.model';
import { FILTER_VALUE } from '../../shared/constants/filters.constants';
import { SelectItem } from 'primeng/api';
import { RuleStatistics } from '../../models/rule-statistics.model';
import { AlarmStatistics } from '../../models/alarm-statistics.model';
import { AlarmDistributionData } from '../../models/alarm-distribution.model';

export const adapter: EntityAdapter<Rule> = createEntityAdapter<Rule>({
  selectId: (rule: Rule) => rule.uid,
});

export interface RuleState extends EntityState<Rule> {
  criticalityOptions: SelectItem[];
  rules: any[];
  filters: {
    criticality: any;
    alarm: string;
    period: string;
    sector: string;
    modelType: string;
    factory: any;
    state: string;
    stage: number | string;
  };
  loading: boolean;
  pagedRules: Rule[];
  pagedTotal: number;
  loadingPage: boolean;
  pageError: string | null;
  isCreateOrUpdateRuleOk: boolean;
  loadingUpdateRule: boolean;
  error: string | null;
  creating: boolean;

  createErrorMessage: string | null;
  updateErrorMessage: string | null;
  statistics: RuleStatistics | null;
  loadingStatistics: boolean;
  statisticsError: string | null;
  alarmStatistics: AlarmStatistics | null;
  loadingAlarmStatistics: boolean;
  alarmStatisticsError: string | null;
  alarmDistribution: AlarmDistributionData | null;
  loadingAlarmDistribution: boolean;
  alarmDistributionError: string | null;
}

export const initialState: RuleState = adapter.getInitialState({
  criticalityOptions: [],
  rules: [],
  pagedRules: [],
  pagedTotal: 0,
  filters: {
    criticality: FILTER_VALUE.ALL,
    alarm: FILTER_VALUE.ACTIVE,
    period: FILTER_VALUE.TODAY,
    sector: FILTER_VALUE.ALL,
    modelType: FILTER_VALUE.ALL,
    factory: FILTER_VALUE.ALL,
    state: FILTER_VALUE.ALL,
    stage: FILTER_VALUE.ALL,
  },
  error: null,
  loading: false,
  isCreateOrUpdateRuleOk: false,
  loadingUpdateRule: false,
  creating: false,
  createErrorMessage: null,
  updateErrorMessage: null,
  loadingPage: true,
  pageError: null,
  statistics: null,
  loadingStatistics: false,
  statisticsError: null,
  alarmStatistics: null,
  loadingAlarmStatistics: false,
  alarmStatisticsError: null,
  alarmDistribution: null,
  loadingAlarmDistribution: false,
  alarmDistributionError: null,
});

export const rulesReducer = createReducer(
  initialState,
  on(
    RulesActions.loadCriticalityOptionssSuccess,
    (state, { criticalityOptions: criticalityOptions }) => ({
      ...state,
      criticalityOptions: criticalityOptions,
      error: null,
    }),
  ),
  on(RulesActions.loadCriticalityOptionsFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(RulesActions.resetFilters, (state) => ({
    ...state,
    rules: [],
    error: null,
  })),

  on(RulesActions.refreshRules, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(RulesActions.loadRules, (state) => {
    if (state.rules.length == 1) {
      return { ...state, loading: false, error: null };
    }
    return { ...state, loading: true, error: null };
  }),

  on(RulesActions.loadRule, (state) => {
    return { ...state, loading: true, error: null };
  }),

  on(RulesActions.loadRulesSuccess, (state, { rules: rules }) => {
    return adapter.setAll(rules, { ...state, loading: false, error: null });
  }),

  on(RulesActions.loadRuleSuccess, (state, { rule }) => {
    const updatedState = adapter.upsertOne(rule, {
      ...state,
      loading: false,
      error: null,
    });
    return {
      ...updatedState,
      pagedRules: updatedState.pagedRules.map((r) =>
        r.uid === rule.uid ? rule : r,
      ),
    };
  }),

  on(RulesActions.dropRuleSuccess, (state, { uid }) =>
    adapter.removeOne(uid, {
      ...state,
      error: null,
    }),
  ),

  on(RulesActions.loadRulesFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  on(RulesActions.loadRulesPage, (state) => ({
    ...state,
    loadingPage: true,
    pageError: null,
  })),

  on(
    RulesActions.loadRulesPageSuccess,
    (state, { rules, totalRecords: total }) => ({
      ...state,
      pagedRules: rules,
      pagedTotal: total,
      loadingPage: false,
      pageError: null,
    }),
  ),

  on(RulesActions.loadRulesPageFailure, (state, { error }) => ({
    ...state,
    loadingPage: false,
    pageError: error,
  })),

  on(RulesActions.clearPagedRules, (state) => ({
    ...state,
    pagedRules: [],
    pagedTotal: 0,
  })),

  on(RulesActions.updateRule, (state) => {
    return { ...state, loadingUpdateRule: true, error: null };
  }),

  on(RulesActions.updateRuleSuccess, (state, { rule }) =>
    adapter.updateOne(
      {
        id: rule.uid,
        changes: rule,
      },
      {
        ...state,
        loadingUpdateRule: false,
        isCreateOrUpdateRuleOk: true,
        error: null,
      },
    ),
  ),

  on(RulesActions.createRule, (state) => ({
    ...state,
    creating: true,
    createErrorMessage: null,
  })),

  on(RulesActions.createRuleSuccess, (state, { rule }) =>
    adapter.addOne(rule, {
      ...state,
      error: null,
      creating: false,
      isCreateOrUpdateRuleOk: true,
      createErrorMessage: null,
    }),
  ),

  on(RulesActions.updateRuleFailure, (state, { message }) => {
    return {
      ...state,
      loadingUpdateRule: false,
      isCreateOrUpdateRuleOk: false,
      updateErrorMessage: message,
    };
  }),

  on(RulesActions.createRuleFailure, (state, { message }) => ({
    ...state,
    creating: false,
    isCreateOrUpdateRuleOk: false,
    createErrorMessage: message,
  })),

  on(
    RulesActions.updateRuleEvaluationSuccess,
    (state, { ruleUid, evaluation }) => {
      const currentRule = state.entities[ruleUid];
      if (!currentRule?.latestAlarm) {
        return state;
      }

      return adapter.updateOne(
        {
          id: ruleUid,
          changes: {
            latestAlarm: {
              ...currentRule.latestAlarm,
              evaluation,
            },
          },
        },
        { ...state },
      );
    },
  ),
  on(
    RulesActions.setFilters,
    (
      ruleState,
      {
        criticality: criticality,
        alarm,
        period,
        sector,
        modelType,
        factory,
        state,
        stage,
      },
    ) => ({
      ...ruleState,
      filters: {
        criticality: criticality,
        alarm,
        period,
        sector,
        modelType,
        factory,
        state,
        stage,
      },
    }),
  ),
  // Rule Statistics reducers
  on(RulesActions.loadRuleStatistics, (state) => ({
    ...state,
    loadingStatistics: true,
    statisticsError: null,
  })),
  on(RulesActions.loadRuleStatisticsSuccess, (state, { statistics }) => ({
    ...state,
    statistics,
    loadingStatistics: false,
    statisticsError: null,
  })),
  on(RulesActions.loadRuleStatisticsFailure, (state, { error }) => ({
    ...state,
    loadingStatistics: false,
    statisticsError: error,
  })),
  // Alarm Statistics reducers
  on(RulesActions.loadAlarmStatistics, (state) => ({
    ...state,
    loadingAlarmStatistics: true,
    alarmStatisticsError: null,
  })),
  on(RulesActions.loadAlarmStatisticsSuccess, (state, { alarmStatistics }) => ({
    ...state,
    alarmStatistics,
    loadingAlarmStatistics: false,
    alarmStatisticsError: null,
  })),
  on(RulesActions.loadAlarmStatisticsFailure, (state, { error }) => ({
    ...state,
    loadingAlarmStatistics: false,
    alarmStatisticsError: error,
  })),
);
